import {
  type Dispatch,
  type FC,
  type MouseEvent,
  type SetStateAction,
  useState,
} from "react";
import type { GridRowId } from "@mui/x-data-grid";
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import { type PropertySummaryRow } from "@/features/propertySummary/types";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { GetPropertySummaryAsFileAPI } from "@/features/propertySummary/api/GetPropertySummaryAsFileAPI";
import { useApiClient } from "@/hooks/useApiClient";
import { Link as RouterLink } from "react-router-dom";

interface PropertySummaryActionCellProps {
  gridId: GridRowId;
  setEditModalOpen: Dispatch<SetStateAction<boolean>>;
  setSheetId: Dispatch<SetStateAction<number | null>>;
  row: PropertySummaryRow;
}

export const PropertySummaryActionCell: FC<PropertySummaryActionCellProps> = (
  props
) => {
  const { gridId, setEditModalOpen, setSheetId, row } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const open = Boolean(anchorEl);
  const { apiClient } = useApiClient();

  const handleClick = (event: MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  // 物件概要書をダウンロードする
  const handleDownload = async (id: number): Promise<void> => {
    setIsDownloading(true);
    try {
      await GetPropertySummaryAsFileAPI(apiClient, {
        id,
        filename: "property_summary.pdf",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleEdit = (id: number): void => {
    handleClose(); // メニューを先に閉じる
    // setSheetIdを設定すると、PropertySummaryListのuseEffectで自動的にモーダルが開かれる
    setSheetId(id);
  };

  // ダウンロードボタンと閲覧・編集ボタンのクリック可否を設定
  const disableDownload = row.status !== "succeeded";
  const disableViewEdit = row.status !== "succeeded";

  const hasRelatedProperty = row.pictureRequest?.status === "SUCCESS";
  const relatedPropertyId = row.pictureRequest?.id ?? null;
  const hasRelatedAllMattersDetail =
    row.allMattersAnalyzeRequest?.status === "completed";
  const relatedAllMattersDetailId = row.allMattersAnalyzeRequest?.id ?? null;

  return (
    <>
      <IconButton aria-label="more-button" onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="action-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={() => {
            void handleDownload(Number(gridId));
          }}
          disabled={disableDownload || isDownloading}
        >
          <ListItemIcon>
            {isDownloading ? <CircularProgress size={20} /> : <DownloadIcon />}
          </ListItemIcon>
          <ListItemText>ダウンロード</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleEdit(Number(gridId));
          }}
          disabled={disableViewEdit}
        >
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText>閲覧・編集</ListItemText>
        </MenuItem>
        {hasRelatedProperty && relatedPropertyId && (
          <MenuItem
            component={RouterLink}
            to={`/acquirebookstatus/${relatedPropertyId}/`}
            onClick={handleClose}
          >
            <ListItemIcon>
              <RequestQuoteIcon />
            </ListItemIcon>
            関係する登記の情報
          </MenuItem>
        )}
        {hasRelatedAllMattersDetail && relatedAllMattersDetailId && (
          <MenuItem
            component={RouterLink}
            to={`/allmatters/detail/${relatedAllMattersDetailId}/`}
            onClick={handleClose}
          >
            <ListItemIcon>
              <LibraryBooksIcon />
            </ListItemIcon>
            関係する全部事項解析の情報
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default PropertySummaryActionCell;
